import type {
  ApprovalStatus,
  ConversationAgentState,
  ConnectionStatus,
  ConnectionType,
  DependencyRelation,
  DeskStatus,
  ExecutionKind,
  ExecutionStatus,
  ResourceClass,
  ReviewDecision,
  RuntimeType,
  SenderType,
  TriggerType,
  WorkItemStatus,
} from '@deskworks/shared-types';
import { DEFAULT_WORK_ITEM_PRIORITY, MAX_WORK_ITEM_PRIORITY } from '@deskworks/shared-types';
import { t, tc } from '@/lib/i18n';

/** Visual tone used by badges and dots — one consistent palette across the app. */
export type Tone = 'neutral' | 'blue' | 'accent' | 'amber' | 'orange' | 'red' | 'green' | 'muted';

export interface StatusMeta {
  label: string;
  tone: Tone;
  /** Shows a pulsing dot (something is actively happening). */
  live?: boolean;
  hint?: string;
}

export const WORK_ITEM_STATUS: Record<WorkItemStatus, StatusMeta> = {
  created: { label: t('Created'), tone: 'neutral', hint: t('Created, but not released for work yet.') },
  ready: { label: t('Ready'), tone: 'blue', hint: t('Waiting for an agent to start.') },
  working: { label: t('Working'), tone: 'accent', live: true, hint: t('An agent is working on it right now.') },
  waiting: { label: t('Waiting'), tone: 'amber', hint: t('Waiting for information — for example from you.') },
  needs_approval: { label: t('Needs approval'), tone: 'orange', hint: t('The agent needs your approval before it can continue.') },
  blocked: { label: t('Blocked'), tone: 'red', hint: t('Can’t continue: something is missing (e.g. a connection) or a dependency isn’t done.') },
  complete: { label: t('Done'), tone: 'green', hint: t('Finished.') },
  failed: { label: t('Failed'), tone: 'red', hint: t('Something went wrong. You can try again.') },
  cancelled: { label: t('Cancelled'), tone: 'muted', hint: t('This was cancelled.') },
};

export const EXECUTION_STATUS: Record<ExecutionStatus, StatusMeta> = {
  queued: { label: t('Queued'), tone: 'neutral' },
  starting: { label: t('Starting'), tone: 'blue', live: true },
  running: { label: t('Running'), tone: 'accent', live: true },
  waiting: { label: t('Waiting'), tone: 'amber' },
  completed: { label: t('Completed'), tone: 'green' },
  failed: { label: t('Failed'), tone: 'red' },
  cancelled: { label: t('Cancelled'), tone: 'muted' },
  lost: { label: t('Lost'), tone: 'red', hint: t('The worker stopped reporting back.') },
};

export const DESK_STATUS: Record<DeskStatus, StatusMeta> = {
  active: { label: t('Active'), tone: 'green' },
  paused: { label: t('Paused'), tone: 'amber' },
  archived: { label: t('Archived'), tone: 'muted' },
};

export const CONNECTION_STATUS: Record<ConnectionStatus, StatusMeta> = {
  unknown: { label: t('Not checked'), tone: 'neutral' },
  healthy: { label: tc('connection', 'Working'), tone: 'green' },
  failing: { label: t('Failing'), tone: 'red' },
  disabled: { label: t('Disabled'), tone: 'muted' },
};

export const APPROVAL_STATUS: Record<ApprovalStatus, StatusMeta> = {
  pending: { label: t('Pending'), tone: 'orange' },
  approved: { label: t('Approved'), tone: 'green' },
  rejected: { label: t('Rejected'), tone: 'red' },
  cancelled: { label: t('Withdrawn'), tone: 'muted' },
};

export const AGENT_STATUS: Record<'active' | 'disabled', StatusMeta> = {
  active: { label: t('Active'), tone: 'green' },
  disabled: { label: t('Disabled'), tone: 'muted' },
};

export const TRIGGER_TYPE: Record<TriggerType, { label: string; description: string }> = {
  manual: { label: t('Manual'), description: t('Only runs when you click “Run now”.') },
  cron: { label: t('Schedule'), description: t('Runs at fixed times (cron expression).') },
  polling: { label: t('Check regularly'), description: t('Looks for new work at a fixed interval.') },
  webhook: { label: 'Webhook', description: t('An external system calls a URL to wake the Desk.') },
};

/** Short provider names for lists and badges. */
export const RUNTIME_TYPE: Record<RuntimeType, string> = {
  anthropic_api: 'Anthropic API',
  openai_api: 'OpenAI / ChatGPT API',
  claude_subscription: t('Claude subscription'),
  codex_subscription: t('ChatGPT subscription'),
  local: t('Local (demo)'),
};

/** "Profile name · provider", without repeating the provider when the profile name already names it. */
export function profileLabel(p: { name: string; runtimeType: RuntimeType; model?: string | null }, withModel = false): string {
  const type = RUNTIME_TYPE[p.runtimeType] ?? p.runtimeType;
  const parts = [p.name];
  // Skip the provider when the name already says it (“Local (demo)”, “OpenAI API”, “Claude Subscription”).
  // The runtime type's own word counts too: a profile named in English (“Local (demo)”) on a German page.
  const brands = [type.split(/[\s/(]/)[0].toLowerCase(), p.runtimeType.split('_')[0]];
  if (!brands.some((brand) => p.name.toLowerCase().includes(brand))) parts.push(type);
  if (withModel && p.model) parts.push(p.model);
  return parts.join(' · ');
}

/**
 * "Runtime · model" for an agent, e.g. "Claude subscription · claude-sonnet-5-5".
 * Uses the agent's effective model (agent choice → runtime default → built-in default).
 */
export function agentRuntimeLabel(
  agent: { effectiveModel?: string | null; model?: string | null; runtimeAutomatic?: boolean },
  profile: { name: string; runtimeType: RuntimeType; model?: string | null } | null | undefined,
): string {
  // "Automatic": the profile is the one the next run takes (docs/features/agents-and-runtimes.md).
  if (!profile) return agent.runtimeAutomatic ? t('Automatic · no runtime available') : t('No runtime');
  const base = agent.runtimeAutomatic ? t('Automatic · {runtime}', { runtime: profileLabel(profile) }) : profileLabel(profile);
  if (profile.runtimeType === 'local') return base;
  const model = agent.effectiveModel ?? agent.model ?? profile.model ?? null;
  return model ? `${base} · ${model}` : base;
}

/** Provider-specific texts for the runtime profile dialog (types not in IMPLEMENTED_RUNTIME_TYPES get “– coming soon”). */
export const RUNTIME_TYPE_FORM: Record<
  RuntimeType,
  { option: string; modelPlaceholder: string; modelHint?: string; credentialLabel: string; credentialHint: string; needsCredential: boolean }
> = {
  anthropic_api: {
    option: t('Anthropic API (API key)'),
    modelPlaceholder: 'claude-opus-5-5',
    credentialLabel: t('API key'),
    credentialHint: t('Your Anthropic API key (starts with “sk-ant-”).'),
    needsCredential: true,
  },
  openai_api: {
    option: t('OpenAI / ChatGPT API (API key)'),
    modelPlaceholder: 'gpt-5',
    credentialLabel: t('API key'),
    credentialHint: t('Your OpenAI API key (starts with “sk-”).'),
    needsCredential: true,
  },
  claude_subscription: {
    option: t('Claude subscription (login)'),
    modelPlaceholder: t('optional, e.g. claude-opus-5-5'),
    modelHint: t('Leave empty to use your subscription’s default model.'),
    credentialLabel: t('Token from “claude setup-token”'),
    credentialHint: t('Run “claude setup-token” in your terminal and paste the token here. Not an API key.'),
    needsCredential: true,
  },
  codex_subscription: {
    option: t('ChatGPT subscription (login)'),
    modelPlaceholder: t('optional, e.g. gpt-5'),
    modelHint: t('Leave empty to use your subscription’s default model.'),
    credentialLabel: t('Credentials from the ChatGPT login'),
    credentialHint: t('The login data of your ChatGPT subscription. Not an API key.'),
    needsCredential: true,
  },
  local: {
    option: t('Local (demo, no AI)'),
    modelPlaceholder: t('not needed'),
    credentialLabel: t('Credentials'),
    credentialHint: t('Not needed for the local demo.'),
    needsCredential: false,
  },
};

export const CONVERSATION_STATE: Record<ConversationAgentState, StatusMeta> = {
  starting: { label: t('Starting'), tone: 'blue', live: true, hint: t('The agent’s container is starting.') },
  thinking: { label: t('Thinking'), tone: 'accent', live: true, hint: t('The agent is working on an answer.') },
  listening: { label: t('Listening'), tone: 'green', hint: t('The agent is awake and waiting for your next message.') },
  sleeping: { label: t('Sleeping'), tone: 'neutral', hint: t('No container running — your next message wakes it up.') },
  failed: { label: t('Error'), tone: 'red', hint: t('Something went wrong with the last answer.') },
};

export const RESOURCE_CLASS: Record<ResourceClass, { label: string; description: string }> = {
  standard: { label: t('Standard'), description: t('For everyday tasks.') },
  coding: { label: t('Coding'), description: t('More computing power, e.g. for programming tasks.') },
};

export const CONNECTION_TYPE: Record<ConnectionType, { label: string; configTemplate: Record<string, unknown> }> = {
  http_api: { label: 'HTTP API', configTemplate: { baseUrl: 'https://api.example.com', authHeader: 'Authorization' } },
  mcp_server: { label: t('MCP server'), configTemplate: { url: 'https://mcp.example.com' } },
  git_repository: { label: t('Git repository'), configTemplate: { url: 'https://gitlab.com/group/project.git', username: 'oauth2', pushBranchPrefix: 'deskworks/' } },
  email: { label: t('Email'), configTemplate: { address: 'team@example.com' } },
  gitlab: { label: 'GitLab', configTemplate: { baseUrl: 'https://gitlab.com', projectId: '' } },
  github: { label: 'GitHub', configTemplate: { owner: '', repo: '' } },
  slack: { label: 'Slack', configTemplate: { channel: '#general' } },
  other: { label: t('Other'), configTemplate: {} },
};

export const DEPENDENCY_RELATION: Record<DependencyRelation, { outgoing: string; incoming: string }> = {
  blocks: { outgoing: t('blocks'), incoming: t('is blocked by') },
  depends_on: { outgoing: t('depends on'), incoming: t('is needed by') },
  related_to: { outgoing: t('related to'), incoming: t('related to') },
};

/** Desk-connection permissions (`read` / `write`); unknown values are shown as-is. */
export const REVIEW_DECISION: Record<ReviewDecision, string> = { approved: t('Approved'), changes_requested: t('Changes requested') };

export const EXECUTION_KIND: Record<ExecutionKind, string> = {
  work_item: t('Work Item'),
  desk_evaluation: t('Lead evaluation'),
  conversation: t('Conversation'),
  follow_up: t('Follow-up chat'),
  lead: t('Lead run'),
};

export const PERMISSION_LABEL: Record<string, string> = { read: t('Read'), write: t('Write') };

export const SENDER_LABEL: Record<SenderType, string> = {
  user: t('You'),
  agent: t('Agent'),
  system: t('System'),
};

export const DESK_AGENT_ROLE = {
  lead: t('Lead Agent'),
  available: t('Available'),
} as const;

export function priorityLabel(priority: number): string {
  return priority === DEFAULT_WORK_ITEM_PRIORITY ? t('Normal ({n})', { n: priority }) : String(priority);
}

/** The priority typed into a form, kept within 0–100; empty or not a number is "normal". */
export function parsePriority(value: string): number {
  const n = Number.parseInt(value, 10);
  return Number.isNaN(n) ? DEFAULT_WORK_ITEM_PRIORITY : Math.min(Math.max(n, 0), MAX_WORK_ITEM_PRIORITY);
}
